import Vue from "vue";
import moment from "moment";
import helper from "@mixins/helper";
import photoParkHelper from "@mixins/gallery/PhotoParkHelper";

/**
 * Creates a full snapshot before a cart quotation mutates visual albums or cart data.
 * The snapshot must allow the UI to return exactly to the previous state when
 * any album quotation fails, regardless of which album triggered the error.
 *
 * @param {Object} params Snapshot parameters.
 * @param {Object} params.state Vuex state object.
 * @returns {Object} Full cart, album, and payment snapshot.
 */
function CreateCartQuotationSnapshot({ state }) {
    return {
        shoppingCart: JSON.parse(JSON.stringify(state.shoppingCart || {})),
        albumes: JSON.parse(JSON.stringify(state.albumes || [])),
        shoppingCartAmount: state.shoppingCartAmount,
        payment: JSON.parse(JSON.stringify(state.payment || {})),
    };
}

/**
 * Restores cart, album, and payment state after a failed quotation.
 *
 * @param {Object} params Restore parameters.
 * @param {Object} params.state Vuex state object.
 * @param {Object} params.snapshot Previously created snapshot.
 * @returns {void}
 */
function RestoreCartQuotationSnapshot({ state, snapshot }) {
    Vue.set(state, "shoppingCart", snapshot.shoppingCart || {});
    Vue.set(state, "albumes", snapshot.albumes || []);
    Vue.set(state, "shoppingCartAmount", snapshot.shoppingCartAmount || 0);
    Vue.set(state, "payment", snapshot.payment || state.payment || {});
}


/**
 * Rebuilds display groups after a cart item is requoted.
 * This prevents stale per-album prices from surviving a currency change when
 * the total amount was updated but the previous DisplayGroups were kept.
 *
 * @param {Object} item Cart item returned by the quote endpoint.
 * @returns {Object} Cart item with refreshed visual price groups.
 */
function RefreshCartDisplayGroups(item) {
    if (!item || !Array.isArray(item.Parks) || !item.Parks.length) {
        return item;
    }

    const amount = Number(item.Amount || 0);
    const sivexPayData = Array.isArray(item.sivexPayDataPaid) && item.sivexPayDataPaid.length
        ? item.sivexPayDataPaid
        : Array.isArray(item.sivexPayData)
            ? item.sivexPayData
            : [];

    const paidParks = Array.isArray(item.PaidParks) && item.PaidParks.length
        ? item.PaidParks
        : item.Parks.filter((park) => {
            return !(park?.WasAutoAddedByPromotion || park?.WasIncludedByPromotion);
        });

    const effectivePaidParks = paidParks.length ? paidParks : item.Parks;
    const paidParkIds = effectivePaidParks.map((park) => Number(park?.Park?.Id));
    const includedParks = item.Parks.filter((park) => {
        return !paidParkIds.includes(Number(park?.Park?.Id));
    });

    const sivexAmount = sivexPayData.reduce((total, pack) => {
        return total + Number(pack?.Amount || 0);
    }, 0);

    const canUseSivexDisplayGroups =
        sivexPayData.length > 0 &&
        (amount === 0 || Math.abs(sivexAmount - amount) <= 0.01);

    if (canUseSivexDisplayGroups) {
        const promotion = photoParkHelper.methods.GetPromotionConfig(sivexPayData);

        item.DisplayGroups = photoParkHelper.methods.BuildDisplayGroups({
            displayParks: item.Parks,
            paidParks: effectivePaidParks,
            sivexPayData,
            promotion,
        });
    } else {
        item.DisplayGroups = [
            {
                groupNumber: 1,
                parks: item.Parks,
                paidParks: effectivePaidParks,
                includedParks,
                subtotal: amount,
                paidUnitPrice:
                    effectivePaidParks.length > 0
                        ? amount / effectivePaidParks.length
                        : amount,
                sivexPayData: null,
            },
        ];
    }

    item.NormalIndividual = effectivePaidParks.length > 0
        ? amount / effectivePaidParks.length
        : amount;

    return item;
}

/**
 * Requotes an existing promotional cart item while respecting Sivex blocks.
 *
 * @param {Object} params Cart parameters.
 * @param {Object} params.data Cart item to requote.
 * @param {Function} params.dispatch Vuex dispatch function.
 * @returns {Promise<Object>} Recalculated cart item.
 */
async function RefreshPromotionCartItem({ data, dispatch }) {
    const paidParks = photoParkHelper.methods.GetPaidParksFromCartItem(data);

    const displayParks =
        Array.isArray(data.Parks) && data.Parks.length
            ? data.Parks
            : paidParks;

    const promotion = photoParkHelper.methods.GetPromotionConfig(
        data.sivexPayDataPaid || data.sivexPayData || []
    );

    const chunkSize = Number(promotion.maxAlbumsPerBatch || 1);

    const quotedAll = await photoParkHelper.methods.QuoteParks({
        selectedParks: displayParks,
        baseItem: {
            ...data,
            Parks: displayParks,
            cantidad: displayParks.length,
            PaidParks: paidParks,
            PromotionApplied: true,
        },
        chunkSize,
    });

    return photoParkHelper.methods.BuildPromotionCartItem({
        quotedAll,
        paidParks,
        displayParks,
        promotion,
        promotionMode: data.PromotionMode || "manual",
        allAvailableParksLength: Number(data.PromotionAvailableAlbumsQuantity || displayParks.length),
    });
}

/**
 * Marks a selection as paid or included according to the album visual flag.
 *
 * @param {Object} params Selection parameters.
 * @param {Object} params.park Selected park/album.
 * @param {Object} params.album Parent album object.
 * @returns {void}
 */
function MarkAlbumSelection({ park, album }) {
    if (park.CanBeIncludedByPromotion) {
        photoParkHelper.methods.MarkParkAsIncludedSelection(park, album);
        return;
    }

    photoParkHelper.methods.MarkParkAsPaidSelection(park, album);
}

/**
 * Synchronizes the visual album state, Sivex quote, and cart item.
 * Applies to both download-code and room-search flows.
 *
 * @param {Object} params Synchronization parameters.
 * @returns {Promise<Object|null>} Quoted item, or null when no albums remain in the cart.
 */
async function SyncAlbumPromotionCartItem({ commit, dispatch, state, idx, sourceType, triggerPark }) {
    const album = state.albumes[idx];
    const isDownloadCode = sourceType === 0;

    if (!album || !triggerPark) {
        return null;
    }

    const cartKey = isDownloadCode
        ? album.code
        : parseInt(triggerPark.Reservation.BookId);
    const currentCartItem = state.shoppingCart[cartKey] || null;

    const photoParks = isDownloadCode
        ? photoParkHelper.methods.GetCurrentAddedDownloadCodeParks(album)
        : photoParkHelper.methods.GetCurrentAddedParks(album);

    const hotels = isDownloadCode
        ? photoParkHelper.methods.GetCurrentAddedDownloadCodeHotels(album)
        : photoParkHelper.methods.GetCurrentAddedHotels(album);

    const selectedUserParks = isDownloadCode
        ? photoParkHelper.methods.GetSelectedUserDownloadCodeParks(album)
        : photoParkHelper.methods.GetSelectedUserParks(album);

    const includedParks = photoParkHelper.methods.GetIncludedPromotionParks(
        album,
        { excludeUnlocked: isDownloadCode }
    );

    album.FreeHotels = isDownloadCode
        ? photoParkHelper.methods.HasSelectedRegularDownloadCodePark(album.park)
        : photoParkHelper.methods.HasSelectedRegularPark(album.park);

    const parkSelected = hotels.length && !photoParks.length ? hotels : photoParks;

    if (!parkSelected.length) {
        delete state.shoppingCart[cartKey];
        album.FreeHotels = false;
        photoParkHelper.methods.ClearManualIncludeCandidates(album);
        return null;
    }

    const pax = triggerPark.Reservation.Pax;
    const isOnlyHotel = hotels.length && !photoParks.length;
    const parkId = isOnlyHotel
        ? triggerPark.Park.Id
        : parseInt(triggerPark.Reservation.HotelId);

    const item = {
        code: isDownloadCode ? album.code : undefined,
        Room: isDownloadCode
            ? parseInt(triggerPark.Reservation.Room)
            : parseInt(album.code),
        BookId: parseInt(triggerPark.Reservation.BookId),
        cantidad: parkSelected.length,
        Pax: pax,
        ParkId: parkId,
        ParkPrefix: helper.methods.getParkPrefix(parkId),
        Parks: parkSelected,
        IsOnlyHotel: isOnlyHotel,
        type: sourceType,
        date: moment().format("YYYY-MM-DD"),
        Id: 0,
        Amount: 0,
        NormalIndividual: 0,
        RateKey: "",
    };

    if (isOnlyHotel) {
        const priceSivex = await dispatch("GetNewPrice", item);

        parkSelected.forEach((hotel) => {
            Vue.set(hotel, "Amount", priceSivex.Amount);
            Vue.set(hotel, "RateKey", priceSivex.RateKey);
        });

        priceSivex.Amount = parkSelected.reduce(
            (acc, { Amount }) => acc + Number(Amount || 0),
            0
        );

        commit("setCarrito", priceSivex);

        return priceSivex;
    }

    const allAvailableParks = isDownloadCode
        ? photoParkHelper.methods.GetAllAvailableDownloadCodeParks(album)
        : photoParkHelper.methods.GetAllReservationParks(album);

    const quoteBaseParks = selectedUserParks.length
        ? selectedUserParks
        : photoParks;

    let initialQuote = null;
    let promotion = photoParkHelper.methods.GetPromotionConfig(
        currentCartItem?.sivexPayDataPaid || currentCartItem?.sivexPayData || []
    );

    if (
        !photoParkHelper.methods.HasPromotionConfig(promotion) &&
        quoteBaseParks.length
    ) {
        initialQuote = await photoParkHelper.methods.QuoteParks({
            selectedParks: quoteBaseParks,
            baseItem: {
                ...item,
                Parks: quoteBaseParks,
                cantidad: quoteBaseParks.length,
                PaidParks: selectedUserParks,
                PromotionApplied: false,
            },
        });

        promotion = photoParkHelper.methods.GetPromotionConfig(
            initialQuote.sivexPayData
        );
    }

    const isPaidTriggerAtPromotionThreshold =
        triggerPark.WasSelectedByUser &&
        photoParkHelper.methods.HasPromotionConfig(promotion) &&
        selectedUserParks.length === Number(promotion.requiredPurchases || 0);

    if (!initialQuote && isPaidTriggerAtPromotionThreshold) {
        initialQuote = await photoParkHelper.methods.QuoteParks({
            selectedParks: quoteBaseParks,
            baseItem: {
                ...item,
                Parks: quoteBaseParks,
                cantidad: quoteBaseParks.length,
                PaidParks: selectedUserParks,
                PromotionApplied: false,
            },
        });
    }

    const promotionMode = photoParkHelper.methods.GetPromotionMode({
        promotion,
        selectedParksLength: selectedUserParks.length,
        availableParksLength: allAvailableParks.length,
    });

    let priceSivex;

    if (promotionMode === "auto") {
        const displayParks = photoParkHelper.methods.BuildAutoAddedParks({
            selectedParks: selectedUserParks,
            allAvailableParks,
            maxFreeAlbums: promotion.maxFreeAlbums,
        });

        photoParkHelper.methods.MarkAutoPromotionParks({
            displayParks,
            paidParks: selectedUserParks,
            album,
        });

        album.FreeHotels = true;

        const quotedAll = await photoParkHelper.methods.QuoteParks({
            selectedParks: displayParks,
            baseItem: {
                ...item,
                Parks: displayParks,
                cantidad: displayParks.length,
                PaidParks: selectedUserParks,
                PromotionApplied: true,
            },
            chunkSize: promotion.maxAlbumsPerBatch,
        });

        priceSivex = photoParkHelper.methods.BuildPromotionCartItem({
            quotedAll,
            paidParks: selectedUserParks,
            displayParks,
            promotion,
            promotionMode,
            allAvailableParksLength: allAvailableParks.length,
        });
    } else if (promotionMode === "manual") {
        photoParkHelper.methods.UpdateManualIncludeCandidates({
            album,
            promotion,
            allAvailableParks,
            paidParks: selectedUserParks,
            includedParks,
        });

        const displayParks = photoParks;
        const canReuseInitialQuote =
            initialQuote &&
            photoParkHelper.methods.SameParkListById(
                quoteBaseParks,
                displayParks
            );

        const quotedAll = canReuseInitialQuote
            ? initialQuote
            : await photoParkHelper.methods.QuoteParks({
                selectedParks: displayParks,
                baseItem: {
                    ...item,
                    Parks: displayParks,
                    cantidad: displayParks.length,
                    PaidParks: selectedUserParks,
                    PromotionApplied: true,
                },
                chunkSize: promotion.maxAlbumsPerBatch,
            });

        priceSivex = photoParkHelper.methods.BuildPromotionCartItem({
            quotedAll,
            paidParks: selectedUserParks,
            displayParks,
            promotion,
            promotionMode,
            allAvailableParksLength: allAvailableParks.length,
        });
    } else {
        photoParkHelper.methods.ClearManualIncludeCandidates(album);
        photoParkHelper.methods.RemovePromotionIncludedParks(album);

        const regularParks = isDownloadCode
            ? photoParkHelper.methods.GetSelectedUserDownloadCodeParks(album)
            : photoParkHelper.methods.GetSelectedUserParks(album);

        if (!regularParks.length) {
            delete state.shoppingCart[cartKey];
            album.FreeHotels = false;
            return null;
        }

        const canReuseInitialQuote =
            initialQuote &&
            photoParkHelper.methods.SameParkListById(
                quoteBaseParks,
                regularParks
            );

        const regularQuote = canReuseInitialQuote
            ? initialQuote
            : await photoParkHelper.methods.QuoteParks({
                selectedParks: regularParks,
                baseItem: {
                    ...item,
                    Parks: regularParks,
                    cantidad: regularParks.length,
                    PaidParks: regularParks,
                    PromotionApplied: false,
                },
            });

        priceSivex = photoParkHelper.methods.BuildRegularCartItem({
            quoted: regularQuote,
            paidParks: regularParks,
        });
    }

    Vue.set(triggerPark, "Amount", priceSivex.Amount);
    commit("setCarrito", priceSivex);

    return priceSivex;
}

/**
 * Gets the active block size used to know which included albums must be removed.
 *
 * @param {Object} params Cart and promotion parameters.
 * @returns {number} Maximum album count per block.
 */
function GetPromotionBlockSize({ currentCartItem, promotion }) {
    return Number(
        currentCartItem?.PromotionMaxAlbumsPerBatch ||
        promotion?.maxAlbumsPerBatch ||
        0
    );
}

/**
 * Removes the included albums associated with the removed paid album block.
 *
 * @param {Object} params Removal parameters.
 * @returns {number} Number of included albums removed.
 */
function RemoveIncludedAlbumsFromRemovedBlock({ album, park, currentCartItem, excludeUnlocked = false }) {
    const promotion = photoParkHelper.methods.GetPromotionConfig(
        currentCartItem?.sivexPayDataPaid || currentCartItem?.sivexPayData || []
    );

    const maxAlbumsPerBatch = GetPromotionBlockSize({
        currentCartItem,
        promotion,
    });

    return photoParkHelper.methods.RemovePromotionIncludedParksFromRemovedParkBlock({
        album,
        removedPark: park,
        maxAlbumsPerBatch,
        excludeUnlocked,
    });
}

/**
 * Rebuilds the promotion flags after a paid album removal.
 * This lets the next albums move up to the previous block and prevents paid
 * packages from being displayed as included albums.
 *
 * @param {Object} params Normalization parameters.
 * @param {Object} params.album Parent album object.
 * @param {Object|null} params.currentCartItem Current cart item used to recover promotion metadata.
 * @param {boolean} params.excludeUnlocked Whether already-unlocked albums must be ignored.
 * @returns {{addedParks: Array<Object>, paidParks: Array<Object>, includedParks: Array<Object>}} Normalized groups.
 */
function NormalizeAlbumPromotionBlocks({ album, currentCartItem, excludeUnlocked = false }) {
    const promotion = photoParkHelper.methods.GetPromotionConfig(
        currentCartItem?.sivexPayDataPaid || currentCartItem?.sivexPayData || []
    );

    return photoParkHelper.methods.NormalizeManualPromotionSelections({
        album,
        promotion,
        excludeUnlocked,
    });
}

export default {
    RefreshCartDisplayGroups,
    RefreshPromotionCartItem,
    MarkAlbumSelection,
    SyncAlbumPromotionCartItem,
    RemoveIncludedAlbumsFromRemovedBlock,
    NormalizeAlbumPromotionBlocks,
    CreateCartQuotationSnapshot,
    RestoreCartQuotationSnapshot,
};
